import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useQuery } from 'react-query';
import SearchIcon from '@mui/icons-material/Search';
import SortIcon from '@mui/icons-material/Sort';
import WatchIcon from '@mui/icons-material/Watch';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Button, Container, Typography, Grid, TextField, InputAdornment,
  FormControl, InputLabel, Select, MenuItem, Pagination, Card,
  CardMedia, CardContent, CardActionArea, Chip, Stack, Skeleton,
  Alert, Box
} from '@mui/material';
import { fetchWatches } from '../../services/ebayService';
import { useTranslation } from 'react-i18next';

const getImageUrl = (listing) => {
  const raw = listing.image || (listing.images && listing.images[0]) || '';
  return raw ? raw.replace(/s-l\d+/, 's-l500') : 'https://placehold.co/400x400?text=Orologio';
};

const removeDuplicates = (listings) => {
  const seen = new Set();
  return listings.filter(item => {
    if (!item || !item.id) return false;
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
};

const fetchListings = async () => {
  const listings = await fetchWatches();
  return removeDuplicates(listings);
};


const WatchMarketplacePage = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('price-desc');
  const selectedCategory = searchParams.get('categoria') || 'All';
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;
  const navigate = useNavigate();

  const setSelectedCategory = (cat) => {
    if (cat === 'All') {
      setSearchParams({});
    } else {
      setSearchParams({ categoria: cat });
    }
    setCurrentPage(1);
  };

  const CATEGORIES = [
    { key: 'All', label: t('marketplace.categories.all') },
    { key: 'Watches', label: t('marketplace.categories.watches') },
    { key: 'Watch Box', label: t('marketplace.categories.watchBox') },
    { key: 'Watch Spare Parts', label: t('marketplace.categories.spareParts') },
    { key: 'Watch Accessories', label: t('marketplace.categories.accessories') },
    { key: 'Other', label: t('marketplace.categories.other') },
  ];

  const { data: listings = [], isLoading, error, refetch } = useQuery(
    'marketplaceListings',
    fetchListings,
    {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    }
  );

  const filteredListings = listings
    .filter(listing => {
      const words = searchTerm.toLowerCase().split(/\s+/).filter(Boolean);
      const haystack = `${listing.title} ${listing.brand || ''} ${listing.reference || ''}`.toLowerCase();
      const matchesSearch = words.length === 0 || words.every(w => haystack.includes(w));
      const matchesCategory = selectedCategory === 'All' || listing.category === selectedCategory;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'price-asc': return a.price - b.price;
        case 'price-desc': return b.price - a.price;
        case 'newest': return new Date(b.lastUpdated || b.listingDate) - new Date(a.lastUpdated || a.listingDate);
        default: return 0;
      }
    });

  const totalPages = Math.ceil(filteredListings.length / itemsPerPage);
  const currentListings = filteredListings.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCategoryChange = (key) => {
    setSelectedCategory(key);
  };

  const handleListingClick = (listing) => {
    if (listing && listing.id) {
      const slug = listing.title
        ? listing.id + '-' + listing.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60)
        : listing.id;
      navigate(`/mercato/${slug}`, { state: { listing } });
    }
  };

  if (isLoading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Skeleton variant="rectangular" width="100%" height={56} sx={{ mb: 2 }} />
        <Grid container spacing={3}>
          {Array.from({ length: 9 }).map((_, i) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={`loading-${i}`}>
              <Skeleton variant="rectangular" height={300} sx={{ mb: 1 }} />
              <Skeleton width="60%" />
              <Skeleton width="40%" />
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, textAlign: 'center' }}>
        <Alert severity="error" sx={{ mb: 3 }}>{t('marketplace.error')}</Alert>
        <Button variant="contained" color="primary" onClick={() => refetch()} startIcon={<RefreshIcon />}>
          {t('marketplace.reload')}
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Helmet>
        <title>Marketplace Orologi Vintage e di Lusso | WatchPassion</title>
        <meta name="description" content="Acquista orologi vintage e di lusso: Rolex, Omega, Audemars Piguet e molto altro. Tutti autenticati e verificati." />
        <link rel="canonical" href="https://watchpassion.net/mercato" />
      </Helmet>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant={{ xs: 'h5', sm: 'h4' }} component="h1" gutterBottom>
          <WatchIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          {t('marketplace.title')}
        </Typography>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => refetch()} size="small">
          {t('marketplace.refresh')}
        </Button>
      </Box>

      {/* Filtri categoria – scroll orizzontale su mobile */}
      <Box sx={{ mb: 3, overflowX: 'auto', pb: 0.5 }}>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'nowrap', width: 'max-content' }}>
          {CATEGORIES.map(cat => (
            <Chip
              key={cat.key}
              label={cat.label}
              onClick={() => handleCategoryChange(cat.key)}
              color={selectedCategory === cat.key ? 'primary' : 'default'}
              variant={selectedCategory === cat.key ? 'filled' : 'outlined'}
            />
          ))}
        </Stack>
      </Box>

      {/* Barra ricerca e ordinamento */}
      <Box sx={{ mb: 4, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
        <TextField
          placeholder={t('marketplace.search')}
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ flex: 1 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
          }}
        />
        <FormControl sx={{ minWidth: { xs: '100%', sm: 180 } }} size="small">
          <InputLabel id="sort-by-label">{t('marketplace.sortBy')}</InputLabel>
          <Select
            labelId="sort-by-label"
            value={sortBy}
            label={t('marketplace.sortBy')}
            onChange={(e) => setSortBy(e.target.value)}
            startAdornment={<InputAdornment position="start"><SortIcon /></InputAdornment>}
          >
            <MenuItem value="relevance">{t('marketplace.relevance')}</MenuItem>
            <MenuItem value="price-asc">{t('marketplace.priceAsc')}</MenuItem>
            <MenuItem value="price-desc">{t('marketplace.priceDesc')}</MenuItem>
            <MenuItem value="newest">{t('marketplace.newest')}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {currentListings.length > 0 ? (
        <>
          <Grid container spacing={3}>
            {currentListings.map((listing, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={`${listing.id}-${index}`}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardActionArea
                    onClick={() => handleListingClick(listing)}
                    sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
                  >
                    <CardMedia
                      component="img"
                      height="200"
                      image={getImageUrl(listing)}
                      alt={listing.title}
                      sx={{ width: '100%', objectFit: 'cover', backgroundColor: '#f5f5f5' }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://placehold.co/400x400?text=Immagine+non+disponibile';
                      }}
                    />
                    <CardContent sx={{ width: '100%' }}>
                      <Typography gutterBottom variant="h6" component="div" noWrap>
                        {listing.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {listing.brand} {listing.reference && `Ref: ${listing.reference}`}
                      </Typography>
                      <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
                        {new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(listing.price || 0)}
                      </Typography>
                      {listing.condition && (
                        <Chip label={listing.condition} size="small" sx={{ mt: 1 }} />
                      )}
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>

          {totalPages > 1 && (
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
              <Pagination count={totalPages} page={currentPage} onChange={handlePageChange} color="primary" showFirstButton showLastButton />
            </Box>
          )}
        </>
      ) : (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <ShoppingBagIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>{t('marketplace.noResults')}</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>{t('marketplace.noResultsDesc')}</Typography>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => refetch()}>
            {t('marketplace.reload')}
          </Button>
        </Box>
      )}
    </Container>
  );
};

export default WatchMarketplacePage;
